import { Form, Layout } from 'antd';
import { Content } from 'antd/es/layout/layout';
import { useEffect, useMemo, useState } from 'react';

import { SDetailLayout } from 'app/components/StaycationUI';
import { AssetDetailTitle } from 'app/components/commons/AssetDetailTitle/AssetDetailTitle';
import { Delete } from 'app/components/commons/ExtraButtons/Delete/Delete';
import { Mode } from 'app/components/commons/Hotel/Types';
import { MenuCustom } from 'app/components/commons/MenuCustom/MenuCustom';
import { Spinner } from 'app/components/commons/Spinner/Spinner';
import { SCROLL_TO_FIRST_ERROR } from 'app/utils/scroll';

import './Experience.scss';
import { DurationChangeModal } from './_components/BasicInformations/DurationChangeModal';
import { ExperienceForm } from './_components/ExperienceForm';
import { ExperienceSwitchModal } from './_components/ExperienceSwitchModal';
import { useExperienceActions } from './hook/useExperienceActions';
import { useExperienceData } from './hook/useExperienceData';
import {
  isFormDirty,
  registerFormDirtyCallback,
  resetFormDirty,
  resetValidationErrors,
  unregisterFormDirtyCallback,
} from './hook/useValidationStore';
import { MenuItemName } from './menuItem';

type PageContext = {
  isCreatePage: boolean;
  experienceId?: number;
};

type DisplayConfig = {
  title?: string;
  buttonSaveText?: string;
  showDelete?: boolean;
};

type MenuItem = {
  name: MenuItemName;
};

const MENU_ITEMS = [
  { name: MenuItemName.General },
  { name: MenuItemName.Accessibility },
  { name: MenuItemName.Availability },
  { name: MenuItemName.Copywriting },
  { name: MenuItemName.Medias },
];

const useHeaderLogic = ({
  isCreatePage,
  showDelete,
  experienceId,
}: PageContext & Pick<DisplayConfig, 'showDelete'>) => {
  const shouldShowEdit = !isCreatePage;
  const shouldShowCancel = !isCreatePage;
  const shouldShowDelete = !isCreatePage && showDelete;

  return {
    shouldShowEdit,
    shouldShowCancel,
    shouldShowDelete,
    deleteEntityDescription: `Experience #${experienceId}`,
  };
};

export const Experience = () => {
  const [menu, setMenu] = useState<MenuItem>(MENU_ITEMS[0]);
  const [pendingMenu, setPendingMenu] = useState<MenuItem | null>(null);
  const [showModal, setShowModal] = useState(false);

  const experienceData = useExperienceData();
  const {
    experienceId,
    isCreatePage,
    initialValues,
    isDataLoaded,
    title,
    buttonSaveText,
    experiencesCategories,
    fetchedExperience,
    hotel,
  } = experienceData;

  const experienceActions = useExperienceActions({
    experienceId,
    isCreatePage,
    initialValues,
    activeTab: menu.name,
  });

  const {
    form,
    mode,
    canSubmit,
    isLoading,
    onCancel,
    handleDelete,
    handleSave,
    saveActiveTab,
    enterEditMode,
    enterViewMode,
    setCanSubmit,
    formatName,
    durationChange,
    durationChangeEndsNextDay,
    handleDurationModalConfirm,
    handleDurationModalCancel,
  } = experienceActions;

  const {
    shouldShowEdit,
    shouldShowCancel,
    shouldShowDelete,
    deleteEntityDescription,
  } = useHeaderLogic({
    isCreatePage,
    experienceId,
    showDelete: true,
  });

  useEffect(() => {
    resetFormDirty();
    registerFormDirtyCallback(() => setCanSubmit(true));

    return () => unregisterFormDirtyCallback();
  }, [setCanSubmit]);

  const exp = Form.useWatch([], form) || fetchedExperience;

  const formattedTitle = useMemo(() => {
    if (!exp) {
      return undefined;
    }

    return formatName(
      exp,
      experiencesCategories?.find((cat) => cat.id === exp?.categoryId)?.name ||
        ''
    );
  }, [exp, experiencesCategories, formatName]);

  if (!experienceId && !isCreatePage) {
    return null;
  }

  const handleMenuChange = (newMenu: MenuItem) => {
    const isDirty =
      mode === Mode.Edit && (form.isFieldsTouched(false) || isFormDirty());

    if (isDirty) {
      setPendingMenu(newMenu);
      setShowModal(true);
    } else {
      setMenu(newMenu);
    }
  };

  const handleModalSave = async () => {
    const targetMenu = pendingMenu;

    const result = await saveActiveTab(() => {
      if (targetMenu) {
        setMenu(targetMenu);
      }
    });

    if (result === 'failed') {
      return;
    }

    setShowModal(false);
    setPendingMenu(null);
  };

  const handleModalDiscard = () => {
    form.resetFields();
    resetValidationErrors();
    resetFormDirty();
    enterViewMode();

    if (pendingMenu) {
      setMenu(pendingMenu);
    }

    setShowModal(false);
    setPendingMenu(null);
  };

  const renderMenu = () => (
    <MenuCustom
      menuItems={MENU_ITEMS.map((item) => ({
        ...item,
        disabled: !isDataLoaded,
      }))}
      selectedMenu={menu}
      setSelectedMenu={handleMenuChange}
    />
  );

  return (
    <Layout className="master-page experience-page">
      <SDetailLayout
        title={
          <AssetDetailTitle
            assetTitle={formattedTitle || title}
            hotelId={hotel?.id}
            hotelName={hotel?.name}
            isNewDataModel={true}
          />
        }
        mode={mode}
        onEdit={shouldShowEdit ? enterEditMode : undefined}
        onSave={handleSave}
        onCancel={shouldShowCancel ? onCancel : undefined}
        buttonSaveText={buttonSaveText}
        headerExtra={
          shouldShowDelete && (
            <Delete
              entityDescription={deleteEntityDescription}
              onDelete={handleDelete}
            />
          )
        }
        isDirty
        backRoute="/activities"
        isValid={canSubmit}
        saveDisabled={!canSubmit}
        isLoading={isLoading}
        headerMenu={!isCreatePage && renderMenu()}
      >
        <Content>
          {isDataLoaded ? (
            <Form
              form={form}
              className="experience-content-form"
              layout="vertical"
              initialValues={initialValues}
              scrollToFirstError={SCROLL_TO_FIRST_ERROR}
              onFieldsChange={(changedFields) => {
                resetValidationErrors();

                if (changedFields.length > 0) {
                  setCanSubmit(true);
                }
              }}
            >
              <ExperienceForm itemName={menu.name} />
            </Form>
          ) : (
            <Spinner />
          )}
        </Content>
      </SDetailLayout>

      <ExperienceSwitchModal
        open={showModal}
        onSave={handleModalSave}
        onDiscard={handleModalDiscard}
        onCancel={() => {
          setShowModal(false);
          setPendingMenu(null);
        }}
      />

      <DurationChangeModal
        change={durationChange}
        endsNextDay={durationChangeEndsNextDay}
        onConfirm={handleDurationModalConfirm}
        onCancel={handleDurationModalCancel}
      />
    </Layout>
  );
};
